import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { NamePrefixModifier, withInjectedProps } from '../../../../Core/InjectedPropsContext';

import { WorksheetImagesAndRemarksSection, WorksheetTestedBySection } from 'Components/Worksheets/Core';
import { hasNonEmptyValue } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import { not } from 'ramda';
import { PageSection } from '../../../../Layout';
import { IWorksheetComponent, WorksheetRootComponent } from '../../../Core/types';
import ids from '../../../QestIds';
import { PortionDebugViewButton } from '../../Components';
import { createSeparationCountSelector } from '../../Helpers';
import {
  PortionSievingPageSection,
  ResultsPageSection,
  SeparationPageSection,
  SievingPreparationPageSection,
  SpecimenPreparationPageSection,
  SubspecimenMassesPageSection,
  TestPreparationPageSection,
  WholePortionMassesPageSection,
} from './Sections';

const isDevMode = process.env.REACT_APP_ENABLE_DEV_MODE === 'true';
const qestIds = [ids.psdSieveAnalysis.ASTM_D6913.rev2017];

/**
 * Group of sections required to perform separation and sieve produced portions.
 * Recursive as separations are nested.
 *
 * Name prefix should be the path of the portion being separated
 */
let SeparationSectionGroup: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }): ReactElement => {
  const isLastSeparation = useSelector(
    pipe(
      createWorksheetSelector!('Separation.FinePortion.Subspecimen.Separation'),
      (nextSeparation) => hasNonEmptyValue(nextSeparation),
      not
    )
  );
  return (
    <>
      <SeparationPageSection />
      <NamePrefixModifier appendNamePrefix={'Separation'}>
        <NamePrefixModifier appendNamePrefix={'CoarsePortion'}>
          <SievingPreparationPageSection />
          <PortionSievingPageSection />
        </NamePrefixModifier>

        <NamePrefixModifier appendNamePrefix={'FinePortion'}>
          <SubspecimenMassesPageSection />

          <NamePrefixModifier appendNamePrefix={'Subspecimen'}>
            {isLastSeparation ? (
              <>
                <SievingPreparationPageSection />
                <PortionSievingPageSection />
              </>
            ) : (
              <SeparationSectionGroup />
            )}
          </NamePrefixModifier>
        </NamePrefixModifier>
      </NamePrefixModifier>
    </>
  );
};

SeparationSectionGroup = withInjectedProps(SeparationSectionGroup);

let PsdSieveAnalysisAstmD6913Worksheet: WorksheetRootComponent = ({ createWorksheetSelector }) => {
  const isWholePortionSeparated = useSelector(
    pipe(createSeparationCountSelector(createWorksheetSelector!), (c) => c !== 0)
  );

  return (
    <>
      {isDevMode && (
        <PageSection>
          <PortionDebugViewButton />
        </PageSection>
      )}

      <TestPreparationPageSection />
      <SpecimenPreparationPageSection />

      <NamePrefixModifier appendNamePrefix={`WholePortion`}>
        {isWholePortionSeparated ? (
          <SeparationSectionGroup />
        ) : (
          <>
            <WholePortionMassesPageSection />
            <SievingPreparationPageSection />
            <PortionSievingPageSection />
          </>
        )}
      </NamePrefixModifier>

      <PageSection>
        <WorksheetTestedBySection />
      </PageSection>

      <ResultsPageSection />

      <PageSection heading="Images and Remarks">
        <WorksheetImagesAndRemarksSection />
      </PageSection>
    </>
  );
};

PsdSieveAnalysisAstmD6913Worksheet = withInjectedProps(PsdSieveAnalysisAstmD6913Worksheet);
PsdSieveAnalysisAstmD6913Worksheet.qestIds = qestIds;

export default PsdSieveAnalysisAstmD6913Worksheet;
export { PsdSieveAnalysisAstmD6913Worksheet };
